<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_END_OF_DAY_TRANSACTION_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="glAccountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatDateTime(data.value) : '' }}
        </p>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(debit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <template #cell(credit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <template #cell(postingReferenceNumber)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(entryCode)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

      <template #cell(narration)="data">
        <span class="text-nowrap">
          {{ data.value }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      no-close-on-backdrop
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="eodTransactionRules">
        <b-form>

          <b-row class="match-height">
            <!-- Financial Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Financial Date From -->
            <!-- Financial Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- Financial Date To -->
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-from"
                v-model="filter.startDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (From)')"
                :error-name="$t('Transaction Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Transaction Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-to"
                v-model="filter.endDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (To)')"
                :error-name="$t('Transaction Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Transaction Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>
          <b-row class="match-height">
            <!-- Sort Code -->
            <b-col md="6">
              <InputField
                id="entry-code"
                v-model="filter.entryCode"
                :label="$t('Sort Code')"
                :placeholder="$t('Enter Sort Code')"
                type="text"
                for="entry-code"
              />
            </b-col>
            <!-- Sort Code -->
            <!-- Posting Reference Number -->
            <b-col md="6">
              <InputField
                id="posting-reference-number"
                v-model="filter.postingReferenceNumber"
                :label="$t('Posting Reference Number')"
                :placeholder="$t('Enter Reference Number')"
                type="text"
                for="posting-reference-number"
              />
            </b-col>
            <!-- Posting Reference Number -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :label="$t('Branch')"
                :placeholder="$t('Select Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                item="label"
              />
            </b-col>
            <!-- Branch -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { mapActions, mapGetters } from 'vuex'
import { toAmount, toBalance, toFormatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    TablePagination,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, permissionMixin, sweetAlert],
  data() {
    return {
      toBalance,
      toFormatAmount,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('EOD Transaction Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountNumber', sortable: true, label: 'GL/Account Number' },
        { key: 'accountName', sortable: true, label: 'GL/Account Name' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Ref No.' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'narration', sortable: true, label: 'Narration' },
      ],
      filter: {
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        postingReferenceNumber: '',
        branchCode: '',
        entryCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,

    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Financial Date': this.formatCustomDate(item.financialDate),
        'Transaction Date': this.formatDateTime(item.transactionDate),
        'GL/Account No.': item.accountNumber,
        'GL/Account Name': item.accountName,
        'Branch Name': item.branchName,
        Debit: this.toFormatAmount(item.debit),
        Credit: this.toFormatAmount(item.credit),
        'Posting Ref. No.': item.postingReferenceNumber,
        'Sort Code': item.entryCode,
        Narration: item.narration,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchEODTransactionReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchEODTransactionReport()
    },
  },
  mounted() {
    this.fetchEODTransactionReport()
  },
  methods: {
    ...mapActions({
      getEODTransactions: 'reports/getEODTransactionReports',
      downloadEODTransactionReport: 'reports/downloadEODTransactionReport',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchEODTransactionReport()
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.eodTransactionRules.validate().then(async success => {
        if (success) {
          this.fetchEODTransactionReport()
        }
      })
    },
    async fetchEODTransactionReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getEODTransactions({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.END_OF_DAY_TRANSACTION_REPORT,
          route: 'eodTransactionReport',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadEODTransactionReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.END_OF_DAY_TRANSACTION_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadEODTransactionReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.END_OF_DAY_TRANSACTION_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatAmount: toAmount,
    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
